import {ICartPosition} from "@connox/typescript-library";
import {useRef} from "react";
import * as React from "react";
import {Gateway} from "react-gateway";
import {Tracking} from "../../../tracking/tracking";
import {useTranslation} from "react-i18next";
import {useMediaQuery} from "react-responsive";
import {useCartContext} from "../CartContextProvider/CartContextProvider";
import ItemPrice from "./ItemPrice";
import styles from "./ItemRow.module.scss";
import ItemThumb from "./ItemThumb";
import EnergyLabel from "../../energyLabel/EnergyLabel";

interface IGatewayProps {
  code: string;
  itemId?: string;
}

interface IItemRowProps {
  editable: boolean;
  fixVouchers: ICartPosition[];
  position: ICartPosition;
  relativeVouchers: ICartPosition[];
}

const ItemRow: React.FC<IItemRowProps> = (props) => {
  const {editable, fixVouchers, position, relativeVouchers} = props;
  const {item, messages, reference} = position;
  const {cart} = useCartContext();
  const {t} = useTranslation("cart");
  const deviceString = useMediaQuery({query: "(min-width: 768px)"}) ? "Desktop" : "Mobile";
  const into = "CartError" + deviceString;
  const btn = "Button" + deviceString;
  const rowRef = useRef<HTMLDivElement>(null);

  const EquippedGateway: React.FC<IGatewayProps> = (prop: IGatewayProps) => {
    const {itemId} = prop;
    const toDelete = !item || useMediaQuery({query: "(min-width: 768px)"});

    const clickHandlerMobile = (evt: React.MouseEvent<HTMLAnchorElement>) => {
      handleGotoErrorLinkClick(evt, rowRef);
    };
    const clickHandlerDesktop = async (evt: React.MouseEvent<HTMLAnchorElement>) => {
      evt.preventDefault();
      await cart.removeItem(Number(itemId) || 0);
    };

    const MediaQueryDependentLink: React.FC = () =>
      toDelete ? (
        <a href="#" onClick={clickHandlerDesktop}>
          {t("entfernen")}
        </a>
      ) : (
        <a href="#" onClick={clickHandlerMobile}>
          {t("Zum Fehler springen")}
        </a>
      );

    const gatewayChildren = (
      <React.Fragment>
        {t(prop.code, {itemId})}
        {itemId && (
          <React.Fragment>
            {" "}
            <MediaQueryDependentLink />
          </React.Fragment>
        )}
      </React.Fragment>
    );

    return (
      <React.Fragment>
        <Gateway into={into}>{gatewayChildren}</Gateway>
        <Gateway into={btn}>{gatewayChildren}</Gateway>
      </React.Fragment>
    );
  };

  if (!item) {
    const refItemId = reference?.itemId?.toString();

    return messages.length ? (
      <React.Fragment key={`no_item_${refItemId ?? "no_ref"}`}>
        {messages.map((msg) => (
          <EquippedGateway key={msg.code} code={msg.code} itemId={refItemId} />
        ))}
      </React.Fragment>
    ) : null;
  }

  const ErrorBlock: React.FC = () => {
    const voucherCheck = (vouchers: ICartPosition[], applicable: boolean) => {
      if (applicable) {
        return;
      }
      vouchers.forEach((pos) => {
        // if no error msg on position push one for specific voucher
        // if msg addressing that voucher already present, then ignore
        if (!messages.find((msg) => msg.field === (pos.reference?.voucher?.code ?? ""))) {
          messages.push({
            code: NOT_VOUCHER_APPLICABLE,
            field: pos.reference?.voucher?.code ?? "",
            message: "",
            type: "warning",
          });
        }
      });
    };

    const {isFixedVoucherApplicable, isRelativeVoucherApplicable} = position.discountability;
    voucherCheck(fixVouchers, isFixedVoucherApplicable);
    voucherCheck(relativeVouchers, isRelativeVoucherApplicable);

    if (!messages.length) {
      return null;
    }

    return (
      <ul className="error-line-local">
        {messages.map((msg) => {
          const isVoucherError = msg.code === NOT_VOUCHER_APPLICABLE;
          const idToUse = isVoucherError ? msg.field : item?.itemId.toString();

          return (
            <React.Fragment key={msg.code}>
              <li>{t(msg.code, {itemId: isVoucherError ? idToUse : undefined})}</li>
              {!isVoucherError && <EquippedGateway key={msg.code} code={msg.code} itemId={idToUse} />}
            </React.Fragment>
          );
        })}
      </ul>
    );
  };

  const QuantityUneditable: React.FC<{quantity: number}> = ({quantity}) => {
    return <input className={styles.inputUneditable} type={"text"} readOnly={true} placeholder={quantity.toString()} />;
  };

  const QuantityInput: React.FC = () => {
    const [showNumberInput, setShowNumberInput] = React.useState(false);
    const onQuantityChange = (evt: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
      const value = evt.currentTarget.value;
      if (value === "more" || parseInt(value, 10) < 1 || !value) {
        setShowNumberInput(true);

        return;
      }

      if (typeof quantityTimer !== undefined) {
        window.clearTimeout(quantityTimer);
      }

      quantityTimer = window.setTimeout(() => {
        if (!position.item) {
          return;
        }
        void cart.changeItemQuantity(position.item.itemId, parseInt(value, 10)).then(() =>
          Tracking.sendAddToCart(
            [
              {
                brand: position.item?.manufacturer ?? null,
                id: position.item?.itemId ?? 0,
                name: position.text,
                price: position.price.amount,
                quantity: parseInt(value, 10),
              },
            ],
            position.price.currencyCode,
          ),
        );
      }, 750);
    };
    if (showNumberInput || position.quantity > 10) {
      return <input type={"number"} min="1" onChange={onQuantityChange} defaultValue={position.quantity.toString()} />;
    }

    return (
      <select onChange={onQuantityChange} defaultValue={position.quantity.toString()}>
        {[...Array<number>(10)].map((_, i) => {
          return (
            <option key={i} value={i + 1}>
              {i + 1}
            </option>
          );
        })}
        <option value={"more"}>{t("Mehr")}</option>
      </select>
    );
  };

  const Link = () => (
    <a href={item.url} title={t("{{title}} aufrufen", {title: position.text})}>
      <strong>{position.text}</strong>
    </a>
  );

  const Availability = (): JSX.Element => {
    if (position.availability.isAvailable) {
      return <strong className="green">{t("Auf Lager")}</strong>;
    }

    return (
      <span style={{color: "#333333"}}>
        {t("Wareneingang: {{delivery}}", {delivery: String(position.availability.stockDeliveryTime)})}
      </span>
    );
  };

  // is there an energy efficiency class present
  const energyEfficiency = item.attributes.find((attrib) => attrib.itemAttributeId === 100361);
  const energyLabel = item.images.find((image) => !!image.energyLabel)?.energyLabel || undefined;

  return (
    <div className={styles.itemRow} ref={rowRef}>
      <ItemThumb item={item} />
      <div className={styles.itemDetails}>
        <div className={styles.upper}>
          <div className={styles.upper__left}>
            <Link />
            <div>
              {editable ? <QuantityInput /> : <QuantityUneditable quantity={position.quantity} />} <ErrorBlock />
            </div>
          </div>
          {editable && (
            <div className={styles.upper__right}>
              <span
                className="Svg Svg-Icon-Close"
                onClick={() => {
                  if (position.type === "Voucher") {
                    void cart.removeVoucher(reference?.voucherCode || "");
                  } else {
                    void cart.removeItem(item.itemId).then(() =>
                      Tracking.sendRemoveFromCart({
                        brand: position.item?.manufacturer ?? null,
                        name: position.text,
                        id: item.itemId,
                        price: position.price.amount,
                        quantity: position.quantity,
                      }),
                    );
                  }
                }}
              >
                &#160;
              </span>
            </div>
          )}
        </div>
        <div className={styles.lower}>
          <div className={styles.lower__left}>
            <Availability />
          </div>
          <div className={styles.lower__right}>
            {!!energyEfficiency && (
              <EnergyLabel energyClass={energyEfficiency.value ?? "G"} media={energyLabel} size="small" />
            )}
            <ItemPrice position={position} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemRow;

let quantityTimer: number | undefined;
const NOT_VOUCHER_APPLICABLE = "NOT_VOUCHER_APPLICABLE";

const handleGotoErrorLinkClick = (
  evt: React.MouseEvent<HTMLElement>,
  refObject: React.RefObject<HTMLElement>,
): void => {
  evt.preventDefault();
  if (!refObject.current) {
    return;
  }
  const section = document.querySelector("#ModalRootElement > section");
  if (!section) {
    return;
  }
  const topOffset = refObject.current.getBoundingClientRect().top + section.scrollTop;

  if (typeof section.scrollTo === "function") {
    section.scrollTo({left: 0, top: topOffset, behavior: "auto"});
  } else {
    section.scrollTop = topOffset;
    section.scrollLeft = 0;
  }
};
